// Global Instructions Rule Applied!

import {
  SET_CAPITAL_CALLS,
  APPEND_CAPITAL_CALLS,
  SET_ACTIVE_FILTERS,
  SET_ACTIVE_SORTING,
  CLEAR_ACTIVE_FILTERS,
  UPDATE_CAPITAL_CALL_RECORD,
  REMOVE_CAPITAL_CALL
} from './constants.ts'
import { CapitalCallRecord, CapitalCallRecordFields } from '../../data_models/capital_call_record.ts'
import { FilterValue, SortConfig } from '../../../app_modules/global_configs/tableViewPreferences.ts'

export interface CapitalCallsPaginatedResponse {
  data: CapitalCallRecord[]
  hasMore: boolean
  totalCount: number
  error?: string
}

export interface CapitalCallsState {
  data: CapitalCallRecord[]
  activeFilters: FilterValue[]
  activeSorting: SortConfig[]
  hasMore: boolean
  currentPage: number
}

type CapitalCallsAction =
  | { type: typeof SET_CAPITAL_CALLS; payload: { response: CapitalCallsPaginatedResponse } }
  | { type: typeof APPEND_CAPITAL_CALLS; payload: { response: CapitalCallsPaginatedResponse } }
  | { type: typeof SET_ACTIVE_FILTERS; payload: { filters: FilterValue[] } }
  | { type: typeof SET_ACTIVE_SORTING; payload: { sorting: SortConfig[] } }
  | { type: typeof CLEAR_ACTIVE_FILTERS }
  | { type: typeof UPDATE_CAPITAL_CALL_RECORD; payload: { capitalCallId: string; updates: Record<string, any> } }
  | { type: typeof REMOVE_CAPITAL_CALL; payload: { capitalCallId: string } }

const capitalCallsReducer = (
  state: CapitalCallsState = {
    data: [],
    activeFilters: [],
    activeSorting: [],
    hasMore: false,
    currentPage: 1
  },
  action: CapitalCallsAction
): CapitalCallsState => {
  const idKey = CapitalCallRecordFields.capital_calls__id
  switch (action.type) {
    case SET_CAPITAL_CALLS: {
      const { response } = action.payload
      return { ...state, data: response.data, hasMore: response.hasMore, currentPage: 1 }
    }

    case APPEND_CAPITAL_CALLS: {
      const { response } = action.payload
      return {
        ...state,
        data: [...state.data, ...response.data],
        hasMore: response.hasMore,
        currentPage: state.currentPage + 1
      }
    }

    case SET_ACTIVE_FILTERS: {
      return { ...state, activeFilters: action.payload.filters }
    }

    case SET_ACTIVE_SORTING: {
      return { ...state, activeSorting: action.payload.sorting }
    }

    case CLEAR_ACTIVE_FILTERS: {
      return { ...state, activeFilters: [] }
    }

    case UPDATE_CAPITAL_CALL_RECORD: {
      const { capitalCallId, updates } = action.payload
      if (!state.data.some((r) => (r as any)[idKey] === capitalCallId)) return state
      return {
        ...state,
        data: state.data.map((r) => ((r as any)[idKey] === capitalCallId ? { ...r, ...updates } : r))
      }
    }

    case REMOVE_CAPITAL_CALL: {
      const { capitalCallId } = action.payload
      return { ...state, data: state.data.filter((r) => (r as any)[idKey] !== capitalCallId) }
    }

    default:
      return state
  }
}

export default capitalCallsReducer

export const setCapitalCalls = (payload: { response: CapitalCallsPaginatedResponse }) => ({
  type: SET_CAPITAL_CALLS,
  payload
})

export const appendCapitalCalls = (payload: { response: CapitalCallsPaginatedResponse }) => ({
  type: APPEND_CAPITAL_CALLS,
  payload
})

export const setCapitalCallsActiveFilters = (payload: { filters: FilterValue[] }) => ({
  type: SET_ACTIVE_FILTERS,
  payload
})

export const setCapitalCallsActiveSorting = (payload: { sorting: SortConfig[] }) => ({
  type: SET_ACTIVE_SORTING,
  payload
})

export const clearCapitalCallsActiveFilters = () => ({ type: CLEAR_ACTIVE_FILTERS })

export const updateCapitalCallRecord = (payload: { capitalCallId: string; updates: Record<string, any> }) => ({
  type: UPDATE_CAPITAL_CALL_RECORD,
  payload
})

export const removeCapitalCall = (payload: { capitalCallId: string }) => ({
  type: REMOVE_CAPITAL_CALL,
  payload
})

export const selectCapitalCallById = (state: any, capitalCallId: string) =>
  (state.capitalCalls?.data ?? []).find(
    (r: any) => r[CapitalCallRecordFields.capital_calls__id] === capitalCallId
  )
